import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '../../Context'
import { AuthContextApi } from '../../Context/api'
import { BuildingOfficeIcon, PlusIcon } from '@heroicons/react/24/outline'
import CardMetricas from '../../Components/Cards/Metricas'
import Header from '../../Components/Header'
import FormBusca from '../../Components/Forms/Busca'
import InvestimentosTable from '../../Components/Table/Investimentos'
import ModalInvestimento from '../../Components/Modal/Investimentos'
import VisualizacaoInvestimento from '../../Components/Modal/Investimentos/Visualizacao'

const Investimentos = () => {
    const navigate = useNavigate()
    const { states, setters } = useContext(AuthContext)
    const {
        getInvestimentosDataSistema,
        getInvestimentos,
        postInvestimentos,
        patchInvestimentos,
    } = useContext(AuthContextApi)

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome_empreendimento')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)

    const filterOptions = [
        { value: 'nome_empreendimento', label: 'Nome' },
        { value: 'codigo_empreendimento', label: 'Código' },
    ]

    const sortOptions = [
        { value: 'data_criacao', label: 'Data de Criação' },
        { value: 'data_atualizacao', label: 'Data de Atualização' },
        { value: 'nome_empreendimento', label: 'Nome' },
        { value: 'codigo_empreendimento', label: 'Código' },
    ]

    const buildParams = (overrides = {}) => new URLSearchParams({
        page: states.page || 0,
        per_page: recordsPerPage,
        sort: selectedSortField,
        sort_dir: sortDirection,
        filter: selectedFilterField,
        search: searchTerm,
        ...overrides,
    }).toString()

    const primeiroLoad = async () => {
        if (states.dataInvestimentos.length > 0) return
        await getInvestimentosDataSistema()
        await getInvestimentos(buildParams({ page: 0, search: '' }))
    }

    const search = async (data) => {
        await getInvestimentos(new URLSearchParams({
            page: 0,
            per_page: data.recordsPerPage,
            sort: data.selectedSortField,
            sort_dir: data.sortDirection,
            filter: data.selectedFilterField,
            search: data.searchTerm || '',
        }).toString())
    }

    const clear = async () => {
        setters.setPage(0)
        await getInvestimentos(buildParams({
            page: 0, search: '', sort: 'data_criacao',
            sort_dir: 'desc', filter: 'nome_empreendimento', per_page: 10,
        }))
    }

    const next = () => setters.setPage(states.page + 1)
    const previous = () => { if (states.page > 0) setters.setPage(states.page - 1) }

    const addInvestimento = () => {
        setters.setIsEdit(false)
        setters.setInvestimentos({})
        setters.setModalInvestimentos(true)
    }

    const editInvestimento = (data) => {
        setters.setIsEdit(true)
        setters.setInvestimentos(data)
        setters.setModalInvestimentos(true)
    }

    const viewInvestimento = (data) => {
        setters.setInvestimentos(data)
        setters.setModalViewInvestimentos(true)
    }

    const navigateInvestimento = (data) => {
        navigate(`/investimentos/investimento/${data.id}`)
    }

    const createHandler = async (data) => {
        const ok = await postInvestimentos(data)
        if (ok) {
            setters.setModalInvestimentos(false)
            await getInvestimentos(buildParams())
        }
    }

    const editHandler = async (id, data) => {
        const ok = await patchInvestimentos(id, data)
        if (ok) {
            setters.setModalInvestimentos(false)
            setters.setIsEdit(false)
            await getInvestimentos(buildParams())
        }
    }

    useEffect(() => {
        primeiroLoad()
        return () => {
            setters.setDataEstatisticaInvestimentos([])
            setters.setDataInvestimentos([])
            setters.setPage(0)
        }
    }, [])

    useEffect(() => {
        if (states.dataInvestimentos?.length === 0) return
        getInvestimentos(buildParams())
    }, [states.page, recordsPerPage, selectedSortField, sortDirection])

    return (
        <>
            <Header
                title="Investimentos"
                description="Gerencie os empreendimentos cadastrados no sistema"
                icon={BuildingOfficeIcon}
                hasAction={true}
                hasReturn={false}
                buttonAction={addInvestimento}
                buttonText="Novo Investimento"
                buttonIcon={PlusIcon}
                buttonColor="yellow"
            />

            <div className="px-6 pt-5 pb-6">
                <CardMetricas dataMetricas={states.dataEstatisticaInvestimentos} />

                <FormBusca
                    filterOptions={filterOptions}
                    sortOptions={sortOptions}
                    search={search}
                    handleClear={clear}
                    color="yellow"
                    haveData={states.dataInvestimentos?.length > 0}
                    items={{
                        searchTerm, setSearchTerm,
                        selectedFilterField, setSelectedFilterField,
                        selectedSortField, setSelectedSortField,
                        sortDirection, setSortDirection,
                        recordsPerPage, setRecordsPerPage,
                    }}
                />

                <InvestimentosTable
                    data={states.dataInvestimentos ?? []}
                    loading={states.isLoading}
                    page={states.page}
                    recordsPerPage={recordsPerPage}
                    next={next}
                    previous={previous}
                    viewInvestimento={viewInvestimento}
                    editInvestimento={editInvestimento}
                    navigateInvestimento={navigateInvestimento}
                />
            </div>

            <ModalInvestimento
                states={states}
                setters={setters}
                actionCreate={createHandler}
                actionEdit={editHandler}
            />
            <VisualizacaoInvestimento
                states={states}
                setters={setters}
            />
        </>
    )
}

export default Investimentos
